import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Grid,
  Alert,
  Collapse,
  IconButton,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedIcon from "@mui/icons-material/Verified";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { safeJSONParse } from "../../../utils/helper";
import * as XLSX from "xlsx";
import styles from "../agent-response.module.scss";

type Weight = {
  key: string;
  label: string;
  weight: number;
};

type ScoreBreakdown = {
  category_match: number;
  scope_relevance: number;
  historical_spend: number;
  certifications: number;
  ddq_recency: number;
  spend_source?: string;
};

type Supplier = {
  rank: number;
  supplier_id: number;
  name: string;
  overall_score: number;
  match_percentage: number;
  score_breakdown: ScoreBreakdown;
  recommendation_reason: string;
  source: string;
  coupa_categories: string[];
  enriched_categories: string[];
  certifications: string[];
  ddq_status: string;
  ddq_approved_at: string;
  historical_spend_aed: number | null;
  active: boolean;
};

type SearchMeta = {
  total_evaluated: number;
  eligible_after_filter: number;
  matched: number;
  timestamp: string;
  spend_data_available: boolean;
};

type RequirementData = {
  category: string;
  service_type: string;
  capabilities: string[];
  qualifications: string[];
  source: string;
  raw_input_summary: string;
};

type SupplierDiscoveryResultsData = {
  agent_id: string;
  agent_name: string;
  requirement: RequirementData;
  search_meta: SearchMeta;
  suppliers: Supplier[];
  shortlist: any[];
  weights: Weight[];
  editable_fields: string[];
  actions: string[];
};

type SupplierDiscoveryResultsProps = {
  parsedContent: any;
  messageId?: string;
};

export default function SupplierDiscoveryResults({
  parsedContent,
}: SupplierDiscoveryResultsProps): JSX.Element {
  const finalAnswer = parsedContent?.finalAnswer || {};
  const outputData: SupplierDiscoveryResultsData = safeJSONParse(
    finalAnswer?.output || "{}",
  );

  // Destructure data early to avoid TDZ issues
  const { requirement, search_meta, suppliers } = outputData;
  const weights = outputData.weights || [];

  const [expandedSupplier, setExpandedSupplier] = useState<number | null>(null);

  const getScoreColor = useCallback((score: number) => {
    if (score >= 80) return "#00d2aa";
    if (score >= 60) return "#ffa726";
    return "#ef5350";
  }, []);

  const getDDQStatusColor = useCallback((status: string) => {
    if (status === "approved") return "#4caf50";
    if (status === "pending") return "#ff9800";
    return "#f44336";
  }, []);

  const handleDownloadExcel = useCallback(() => {
    // Summary sheet data
    const summaryData = [
      ["Supplier Discovery Results"],
      [""],
      ["Search Summary"],
      ["Category", requirement.category],
      ["Capabilities", requirement.capabilities?.join(", ") || ""],
      ["Qualifications", requirement.qualifications?.join(", ") || ""],
      [""],
      ["Statistics"],
      ["Total Evaluated", search_meta.total_evaluated],
      ["Eligible After Filter", search_meta.eligible_after_filter],
      ["Matched", search_meta.matched],
      ["Timestamp", new Date(search_meta.timestamp).toLocaleString()],
      [""],
      ["Ranking Weights"],
      ...weights.map((w) => [w.label, `${w.weight}%`]),
    ];

    // Suppliers sheet - detailed data
    const suppliersData = [
      [
        "Rank",
        "Supplier Name",
        "Supplier ID",
        "Overall Score",
        "Match %",
        "DDQ Status",
        "DDQ Approved Date",
        "Active",
        "Category Match",
        "Scope Relevance",
        "Historical Spend",
        "Certifications Score",
        "DDQ Recency",
        "Certifications",
        "Coupa Categories",
        "Enriched Categories",
        "Recommendation Reason",
      ],
      ...suppliers.map((s) => [
        s.rank,
        s.name,
        s.supplier_id,
        s.overall_score,
        s.match_percentage,
        s.ddq_status,
        s.ddq_approved_at ? new Date(s.ddq_approved_at).toLocaleDateString() : "",
        s.active ? "Yes" : "No",
        s.score_breakdown.category_match,
        s.score_breakdown.scope_relevance,
        s.score_breakdown.historical_spend,
        s.score_breakdown.certifications,
        s.score_breakdown.ddq_recency,
        s.certifications?.join(", ") || "",
        s.coupa_categories?.join(", ") || "",
        s.enriched_categories?.join(", ") || "",
        s.recommendation_reason,
      ]),
    ];

    // Score breakdown sheet - pivot-style view
    const scoreBreakdownData = [
      [
        "Supplier Name",
        "Overall Score",
        ...weights.map((w) => `${w.label} (${w.weight}%)`),
      ],
      ...suppliers.map((s) => [
        s.name,
        s.overall_score,
        s.score_breakdown.category_match,
        s.score_breakdown.scope_relevance,
        s.score_breakdown.historical_spend,
        s.score_breakdown.certifications,
        s.score_breakdown.ddq_recency,
      ]),
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add Summary sheet
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary["!cols"] = [{ wch: 25 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // Add Suppliers sheet
    const wsSuppliers = XLSX.utils.aoa_to_sheet(suppliersData);
    wsSuppliers["!cols"] = [
      { wch: 6 },  // Rank
      { wch: 30 }, // Name
      { wch: 12 }, // ID
      { wch: 12 }, // Score
      { wch: 10 }, // Match %
      { wch: 12 }, // DDQ Status
      { wch: 15 }, // DDQ Date
      { wch: 8 },  // Active
      { wch: 12 }, // Category Match
      { wch: 12 }, // Scope Relevance
      { wch: 15 }, // Historical Spend
      { wch: 15 }, // Certifications Score
      { wch: 12 }, // DDQ Recency
      { wch: 30 }, // Certifications
      { wch: 40 }, // Coupa Categories
      { wch: 50 }, // Enriched Categories
      { wch: 60 }, // Recommendation
    ];
    XLSX.utils.book_append_sheet(wb, wsSuppliers, "Suppliers");

    // Add Score Breakdown sheet
    const wsScores = XLSX.utils.aoa_to_sheet(scoreBreakdownData);
    wsScores["!cols"] = [
      { wch: 30 }, // Name
      { wch: 12 }, // Overall
      ...weights.map(() => ({ wch: 15 })),
    ];
    XLSX.utils.book_append_sheet(wb, wsScores, "Score Breakdown");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Supplier_Discovery_Results_${timestamp}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  }, [requirement, search_meta, suppliers, weights]);

  return (
    <Box className={styles.pressReleaseContent}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "#f8fffe",
          border: "2px solid #00d2aa",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <EmojiEventsIcon sx={{ fontSize: 32, color: "#00d2aa" }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ color: "#004141", fontWeight: 600, mb: 0.5 }}
            >
              {outputData.agent_name || "Supplier Discovery — Results"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {search_meta.matched} supplier
              {search_meta.matched !== 1 ? "s" : ""} matched your requirements
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadExcel}
            sx={{
              backgroundColor: "#00d2aa",
              "&:hover": { backgroundColor: "#00b894" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Download Excel
          </Button>
        </Box>

        {/* Search Summary */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: "#00d2aa", fontWeight: 600 }}
              >
                {search_meta.total_evaluated}
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                Total Evaluated
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: "#ffa726", fontWeight: 600 }}
              >
                {search_meta.eligible_after_filter}
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                Eligible After Filter
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: "#4caf50", fontWeight: 600 }}
              >
                {search_meta.matched}
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                Matched
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Search Criteria */}
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
          <Typography
            variant="caption"
            sx={{ color: "#666", display: "block", mb: 1.5, fontWeight: 600 }}
          >
            Search Criteria
          </Typography>
          <Grid container spacing={2}>
            {/* Category */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 0.5 }}>
                  Category
                </Typography>
                <Chip
                  label={requirement.category}
                  size="small"
                  sx={{
                    backgroundColor: "#e6f9f5",
                    color: "#00a884",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Grid>

            {/* Capabilities */}
            {requirement.capabilities && requirement.capabilities.length > 0 && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 0.5 }}>
                    Capabilities
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {requirement.capabilities.map((cap, idx) => (
                      <Chip
                        key={idx}
                        label={cap}
                        size="small"
                        sx={{
                          backgroundColor: "#dcfada",
                          color: "#267103",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Qualifications */}
            {requirement.qualifications && requirement.qualifications.length > 0 && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 0.5 }}>
                    Qualifications
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {requirement.qualifications.map((qual, idx) => (
                      <Chip
                        key={idx}
                        label={qual}
                        size="small"
                        sx={{
                          backgroundColor: "#e3f2fd",
                          color: "#1565c0",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>

      {/* Supplier Results */}
      {suppliers && suppliers.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          {suppliers.map((supplier, index) => (
            <Card
              key={supplier.supplier_id}
              elevation={0}
              sx={{
                mb: 2,
                border: "2px solid",
                borderColor: supplier.rank === 1 ? "#00d2aa" : "#e0e0e0",
                borderRadius: 2,
                position: "relative",
                overflow: "visible",
              }}
            >
              {/* Rank Badge */}
              {supplier.rank <= 3 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: 16,
                    backgroundColor:
                      supplier.rank === 1
                        ? "#ffd700"
                        : supplier.rank === 2
                          ? "#c0c0c0"
                          : "#cd7f32",
                    color: "#fff",
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <EmojiEventsIcon sx={{ fontSize: 16 }} />
                  Rank #{supplier.rank}
                </Box>
              )}

              <CardContent sx={{ pt: supplier.rank <= 3 ? 3 : 2 }}>
                {/* Supplier Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#004141", fontWeight: 600, mb: 0.5 }}
                    >
                      {supplier.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        icon={<VerifiedIcon />}
                        label={`DDQ: ${supplier.ddq_status}`}
                        size="small"
                        sx={{
                          backgroundColor:
                            getDDQStatusColor(supplier.ddq_status) + "20",
                          color: getDDQStatusColor(supplier.ddq_status),
                          fontWeight: 500,
                        }}
                      />
                      {supplier.certifications?.map((cert, idx) => (
                        <Chip
                          key={idx}
                          label={cert}
                          size="small"
                          sx={{
                            backgroundColor: "#e3f2fd",
                            color: "#1565c0",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Overall Score */}
                  <Box sx={{ textAlign: "right", minWidth: 100 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: getScoreColor(supplier.overall_score),
                        fontWeight: 700,
                      }}
                    >
                      {supplier.overall_score}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      Overall Score
                    </Typography>
                  </Box>
                </Box>

                {/* Recommendation Reason */}
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon />}
                  sx={{
                    mb: 2,
                    backgroundColor: "#f0f7ff",
                    "& .MuiAlert-icon": { color: "#1976d2" },
                  }}
                >
                  <Typography variant="body2">
                    {supplier.recommendation_reason}
                  </Typography>
                </Alert>

                {/* Score Breakdown */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Score Breakdown
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpandedSupplier(
                          expandedSupplier === index ? null : index,
                        )
                      }
                    >
                      {expandedSupplier === index ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedSupplier === index}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      {Object.entries(supplier.score_breakdown)
                        .filter(([key]) => key !== "spend_source")
                        .map(([key, value]) => {
                          const weight = weights.find((w) => w.key === key);
                          return (
                            <Grid item xs={12} sm={6} key={key}>
                              <Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#666" }}
                                  >
                                    {weight?.label || key}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {value}% (weight: {weight?.weight || 0}%)
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={value as number}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: "#e0e0e0",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: getScoreColor(
                                        value as number,
                                      ),
                                    },
                                  }}
                                />
                              </Box>
                            </Grid>
                          );
                        })}
                    </Grid>
                  </Collapse>
                </Box>

                {/* Categories */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", display: "block", mb: 1 }}
                  >
                    Service Categories
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {supplier.enriched_categories
                      ?.slice(0, 5)
                      .map((cat, idx) => (
                        <Chip
                          key={idx}
                          label={cat}
                          size="small"
                          sx={{
                            backgroundColor: "#f5f5f5",
                            fontSize: "11px",
                            height: 24,
                          }}
                        />
                      ))}
                    {supplier.enriched_categories?.length > 5 && (
                      <Chip
                        label={`+${supplier.enriched_categories.length - 5} more`}
                        size="small"
                        sx={{
                          backgroundColor: "#e0e0e0",
                          fontSize: "11px",
                          height: 24,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No suppliers matched your criteria.
        </Alert>
      )}

      {/* Footer Prompt */}
      {finalAnswer.prompt && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontStyle: "italic",
              textAlign: "center",
              p: 2,
              backgroundColor: "#f8f9fa",
              borderRadius: 1,
            }}
          >
            {finalAnswer.prompt}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
